import { supabase } from './supabase';

/**
 * The app's only route to the pricing vendors.
 *
 * Why this exists: the pricing stack called api.poketrace.com and
 * api.justtcg.com directly, with `process.env.REACT_APP_POKETRACE_API_KEY` and
 * `process.env.REACT_APP_JUSTTCG_API_KEY` in a request header. A CRA env var is
 * substituted into the JavaScript at build time, so both keys were compiled
 * into the bundle and shipped inside the iOS and Android binaries.
 *
 * Neither key has a recognisable prefix, so nothing that scans a bundle for
 * key-shaped strings would ever have found them. That is why the build guard
 * now also refuses a secret-sounding REACT_APP_* name anywhere in src/.
 *
 * Both calls now go through an edge function holding the key in project
 * secrets. Nothing here knows either key.
 *
 * `vendorGet` returns the raw `Response` and the functions mirror the vendor's
 * status and body, so every call site keeps the `res.ok` test, the 429
 * handling and the `await res.json()` parsing it already had. Only the line
 * that builds the request changes.
 *
 * Unlike the AI proxies this sends the anon key when nobody is signed in,
 * because /card/:cardId is browsable by guests and its price ladder has to keep
 * working. The functions treat that as a guest and hold it to a much tighter
 * per-address limit. See supabase/functions/poketrace-proxy for why that trade
 * is acceptable for a subscription vendor and not for a postpay one.
 */
const BASE = process.env.REACT_APP_SUPABASE_URL;
const ANON = process.env.REACT_APP_SUPABASE_ANON_KEY;

async function vendorGet(fn, path) {
  const { data: { session } } = await supabase.auth.getSession();
  // A real session when there is one, so the call is attributed to a person and
  // gets the full budget. The anon key otherwise, which the function counts as
  // a guest.
  const token = session?.access_token || ANON;

  return fetch(`${BASE}/functions/v1/${fn}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      apikey: ANON,
    },
    body: JSON.stringify({ path }),
  });
}

/** @param path a path under the Poketrace v1 base, e.g. `/cards/abc123/listings?country=us` */
export const poketraceGet = (path) => vendorGet('poketrace-proxy', path);

/** @param path a path under the JustTCG v1 base, e.g. `/cards?game=pokemon&tcgplayerId=42` */
export const justtcgGet = (path) => vendorGet('justtcg-proxy', path);
